// Deterministische conformiteitscontrole voor ingrediëntendeclaraties
// volgens EU 1169/2011 (VoedselInformatieVerordening) + NL Warenwet.
// Alle functies zijn puur (geen side-effects) zodat ze auditeerbaar/testbaar zijn.

// De 14 wettelijke allergenen (EU 1169/2011 Bijlage II) zoals in de app benoemd.
// Elke regel: [regex op ingrediënttekst, [allergenen die dit impliceert]]
const ALLERGEN_REGELS = [
  [/\bgluten\b/i, ["Gluten"]],
  [/tarwe|\bwheat\b|tarwebloem|tarwezetmeel|griesmeel|couscous|bulgur|seitan/i, ["Gluten", "Tarwe"]],
  [/\brogge\b|\brye\b/i, ["Gluten", "Rogge"]],
  [/\bgerst\b|\bbarley\b|\bmout\b|\bmalt\b|moutextract/i, ["Gluten", "Gerst"]],
  [/\bhaver\b|\boat(s)?\b/i, ["Gluten", "Haver"]],
  [/\bspelt\b|\bkamut\b/i, ["Gluten", "Spelt"]],
  [/paneermeel|paneer|panko|broodkruim|broodmeel|\brusk\b|beschuit(meel)?/i, ["Gluten", "Tarwe"]],
  [/\bmelk\b|\bmilk\b|lactose|\bwei\b|\bwhey\b|\broom\b|\bkaas\b|\bboter\b|karnemelk|yoghurt|case[iï]ne|\bcasein\b|melkpoeder|melkeiwit|magere\s*melk|volle\s*melk/i, ["Melk"]],
  [/\beieren\b|\bei\b|\begg\b|eigeel|eipoeder|kippenei|scharrelei|volei|heelei|eibestanddeel/i, ["Eieren"]],
  [/\bsoja\b|\bsoya\b|soia|sojalecithine|sojaeiwit|edamame|tofu|tempeh/i, ["Soja"]],
  [/\bpinda('?s)?\b|peanut|arachide/i, ["Pinda's"]],
  [/\bnoten\b|\bnoot\b|amandel|hazelnoot|walnoot|cashew|\bpecan\b|pistache|macadamia|paranoot|\bnut(s)?\b/i, ["Noten"]],
  [/\bvis\b|\bfish\b|zalm|tonijn|kabeljauw|haring|ansjovis|makreel|forel/i, ["Vis"]],
  [/garnaal|garnalen|\bkrab\b|kreeft|schaaldier|scampi|\bgamba('?s)?\b|langoustine/i, ["Schaaldieren"]],
  [/mosterd|mustard/i, ["Mosterd"]],
  [/selderij|selderie|\bcelery\b|knolselderij/i, ["Selderij"]],
  [/sesam|sesame|tahin|tahini/i, ["Sesam"]],
  [/sulfiet|sulfite|\bso2\b|\bs\.?o\.?2\b|metabisulfiet|e2(2[0-8])\b/i, ["Sulfiet (SO2)"]],
  [/lupine|\blupin\b/i, ["Lupine"]],
  [/mossel|mosselen|\boester(s)?\b|inktvis|octopus|weekdier|\bslak(ken)?\b|st\.?\s*jakobs/i, ["Weekdieren"]],
];

// Herken allergenen uit een vrije ingrediënttekst.
export function detectAllergenen(text) {
  const t = String(text || "");
  if (!t.trim()) return [];
  const found = new Set();
  ALLERGEN_REGELS.forEach(([re, allergs]) => { if (re.test(t)) allergs.forEach((a) => found.add(a)); });
  return [...found];
}

const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

// Bouw de wettelijke ingrediëntendeclaratie op uit de recept-onderdelen.
// - Samengestelde ingrediënten (spec.subIngredienten) worden UITGESPLITST naar
//   hun sub-ingrediënten (zonder de commerciële/soortnaam), op hun gewicht in het
//   eindproduct — conform EU 1169/2011 Bijlage VII, deel E, optie zonder eigen naam.
// - Dubbele ingrediënten worden samengeteld.
// - Aflopende volgorde van gewicht.
// parts: [{ naam, grams, spec, water, groep }]
export function explodeDeclaration(parts) {
  const flat = [];
  (parts || []).forEach((pt) => {
    if (!pt || !(pt.grams > 0)) return;
    const subs = pt.spec && Array.isArray(pt.spec.subIngredienten)
      ? pt.spec.subIngredienten.filter((s) => s && s.naam && String(s.naam).trim())
      : [];
    const subTot = subs.reduce((s, x) => s + (Number(x.percentage) || 0), 0);
    if (subs.length && subTot > 0) {
      subs.forEach((s) => {
        const g = (pt.grams * (Number(s.percentage) || 0)) / subTot;
        if (g > 0) flat.push({ naam: String(s.naam).trim(), grams: g, from: pt.naam });
      });
    } else {
      const nm = pt.spec && pt.spec.ingredientnaam ? pt.spec.ingredientnaam : pt.naam;
      if (nm) flat.push({ naam: String(nm).trim(), grams: pt.grams, from: pt.naam });
    }
  });
  // Dubbele samentellen (alleen enkelvoudige namen zonder komma samenvoegen —
  // een niet-uitgesplitste samengestelde tekst met komma's laten we ongemoeid).
  const map = new Map();
  const los = [];
  flat.forEach((f) => {
    if (/,/.test(f.naam)) { los.push(f); return; }
    const key = norm(f.naam);
    if (map.has(key)) map.get(key).grams += f.grams;
    else map.set(key, { ...f });
  });
  const regels = [...map.values(), ...los].sort((a, b) => b.grams - a.grams);
  const totG = regels.reduce((s, r) => s + r.grams, 0) || 1;
  regels.forEach((r) => { r.pct = (r.grams / totG) * 100; });
  const declText = regels
    .map((r) => (r.pct >= 1 ? `${r.naam} ${Math.round(r.pct)}%` : r.naam))
    .filter(Boolean)
    .join(", ");
  return { regels, declText };
}

// Voorstel-allergenen voor een ingrediëntspec: gedetecteerd maar nog niet aangevinkt.
export function suggestAllergenen(spec) {
  if (!spec) return [];
  const txt = [spec.ingredientnaam || ""]
    .concat((spec.subIngredienten || []).map((s) => s && s.naam ? s.naam : ""))
    .join(", ");
  const det = detectAllergenen(txt);
  const have = spec.allergenen || [];
  return det.filter((a) => !have.includes(a));
}

// Volledige conformiteitsanalyse van een productspec.
// Geeft { status: 'ok'|'warn'|'fail', issues:[{level,msg}] }
export function analyseCompliance(ctx) {
  const {
    recept, parts = [], allerg = [], ontbrekend = [], geenRecept,
    declOverride = "", allergOverride = "", heeftVoeding,
  } = ctx || {};
  const issues = [];
  const add = (level, msg, actie = "") => issues.push({ level, msg, actie });

  if (geenRecept) {
    add("fail", "Geen basisrecept gekoppeld — de declaratie kan niet uit de receptuur worden opgebouwd.", "Koppel een recept bij 'Recept' in de productspec, of maak er een aan in het tabblad Recepten.");
  }

  if (recept && recept.ingredienten && recept.ingredienten.length) {
    const tot = recept.ingredienten.reduce((s, i) => s + (Number(i.percentage) || 0), 0);
    if (Math.abs(tot - 100) > 1.0) {
      add("warn", `Receptuur telt op tot ${tot.toFixed(1)}% (verwacht ~100%). Dit beïnvloedt de wettelijke volgorde en de QUID-percentages.`, "Corrigeer de meng-percentages in het recept zodat ze samen 100% zijn (tabblad Recepten).");
    }
    const seen = {}; const dups = [];
    recept.ingredienten.forEach((i) => {
      if (!i.naam) return;
      const k = norm(i.naam);
      if (seen[k]) dups.push(i.naam); else seen[k] = 1;
    });
    if (dups.length) add("warn", `Zelfde ingrediënt komt meermaals voor: ${[...new Set(dups)].join(", ")}.`, "Voeg dubbele ingrediënten samen tot één regel met het totale percentage (tabblad Recepten).");
    const zonderPct = recept.ingredienten.filter((i) => i.naam && !(Number(i.percentage) > 0)).map((i) => i.naam);
    if (zonderPct.length) add("fail", `Percentage ontbreekt bij: ${[...new Set(zonderPct)].join(", ")}. Zonder percentage klopt de wettelijk verplichte aflopende volgorde en de QUID niet.`, "Vul bij elk ingrediënt het gewichtspercentage in (tabblad Recepten → kolom %). De percentages bepalen de volgorde én de QUID-vermelding op het etiket.");
  }

  if (ontbrekend && ontbrekend.length) {
    add("fail", `Geen ingrediëntspecificatie voor: ${ontbrekend.join(", ")}. Hierdoor ontbreken ingrediënten en/of allergenen in de declaratie.`, "Open 'Grondstof- & hulpstof-specs', zoek het ingrediënt en vul de wettelijke ingrediëntnaam, allergenen en voedingswaarde in — of lees de leveranciersspec in met 'Upload PDF/foto'.");
  }

  // Samengesteld ingrediënt (komma in de spec-tekst) maar niet uitgesplitst.
  const nietUitgesplitst = (parts || [])
    .filter((pt) => pt.spec && pt.spec.ingredientnaam && /,/.test(pt.spec.ingredientnaam) && !((pt.spec.subIngredienten || []).length))
    .map((pt) => pt.naam);
  if (nietUitgesplitst.length) {
    add("warn", `Samengesteld ingrediënt nog niet uitgesplitst: ${[...new Set(nietUitgesplitst)].join(", ")}.`, "Open de spec van dit ingrediënt onder 'Grondstof- & hulpstof-specs' en voeg de sub-ingrediënten toe (met %), zodat ze wettelijk correct in de hoofdlijst komen.");
  }

  // Allergeen gedetecteerd in tekst maar niet bevestigd/aangevinkt.
  const detected = new Set();
  (parts || []).forEach((pt) => {
    detectAllergenen((pt.spec && pt.spec.ingredientnaam) || pt.naam).forEach((a) => detected.add(a));
    ((pt.spec && pt.spec.subIngredienten) || []).forEach((s) => detectAllergenen(s && s.naam).forEach((a) => detected.add(a)));
  });
  const confirmed = new Set(allerg);
  const missing = [...detected].filter((a) => !confirmed.has(a));
  if (missing.length) {
    add("fail", `Mogelijk niet-gemarkeerd allergeen gedetecteerd: ${missing.join(", ")}.`, "Open de betreffende ingrediëntspec en vink het allergeen aan bij 'Allergenen' (of verwijder het woord uit de ingrediëntnaam als het niet klopt).");
  }

  // Bron van generieke allergeen-ingrediënten moet benoemd worden (bv. tarwe-afkomst).
  const GENERIEK = [
    [/zetmeel|zetmelen/i, "zetmeel", /tarwe|ma[iï]s|aardappel|rijst|erwt|tapioca/i],
    [/\beiwit\b/i, "eiwit", /melk|soja|tarwe|\bei\b|erwt|lupine/i],
    [/vezel/i, "vezel", /tarwe|bamboe|citrus|erwt|haver/i],
    [/griesmeel|\bbloem\b|\bmeel\b/i, "meel/bloem", /tarwe|rijst|ma[iï]s|boekweit|spelt|rogge/i],
  ];
  const bronPunten = [];
  (parts || []).forEach((pt) => {
    const nm = (pt.spec && pt.spec.ingredientnaam) || pt.naam || "";
    GENERIEK.forEach(([re, label, bronRe]) => { if (re.test(nm) && !bronRe.test(nm)) bronPunten.push(`${label} (${pt.naam})`); });
  });
  if (bronPunten.length) {
    add("warn", `Bron moet benoemd worden bij: ${[...new Set(bronPunten)].join(", ")} — bv. 'tarwezetmeel' i.p.v. 'zetmeel'.`, "Zoek de botanische/dierlijke herkomst op in de leveranciersspec (TDS) van het ingrediënt en pas de 'Wettelijke ingrediëntnaam' aan onder 'Grondstof- & hulpstof-specs' (bv. tarwe, mais, aardappel).");
  }

  if (!heeftVoeding) {
    add("warn", "Geen voedingswaarde berekend — verplichte vermelding op het etiket.", "Vul de voedingswaarde per 100 g in bij de ingrediëntspecs ('Grondstof- & hulpstof-specs'), of lees de leveranciersspec in.");
  }

  if (String(declOverride || "").trim()) {
    add("warn", "De ingrediëntendeclaratie is handmatig overschreven.", "Controleer of de aflopende volgorde op gewicht, de QUID-percentages en de samengestelde ingrediënten nog met de receptuur overeenkomen.");
  }
  if (String(allergOverride || "").trim()) {
    add("warn", "De allergenenlijst is handmatig overschreven.", "Controleer op volledigheid t.o.v. de automatisch gedetecteerde allergenen.");
  }

  const hasFail = issues.some((i) => i.level === "fail");
  const hasWarn = issues.some((i) => i.level === "warn");
  return { status: hasFail ? "fail" : hasWarn ? "warn" : "ok", issues };
}

// Handtekening van de vrijgegeven declaratie+allergenen — wijzigt de inhoud,
// dan vervalt een eerder gegeven akkoord automatisch.
export function declSignature(declText, allergString) {
  return `${String(declText || "").trim()}||${String(allergString || "").trim()}`;
}
